import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Camera, Plus, Save, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Empty, PageHeader, Panel, Tone } from "@/components/shared";
import { useAuth } from "@/lib/auth";
import { api, errMsg, useApi } from "@/lib/api";

const newStage = (n) => ({ id: null, stageName: "", sequenceOrder: n, requiresPhotos: false, checklists: [""] });

function StageEditor({ s, i, total, update, move, remove }) {
  return (
    <div className="rounded-lg border p-4 space-y-3" data-testid={`stage-editor-${i}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="h-6 w-6 rounded-full bg-secondary text-xs font-semibold flex items-center justify-center num">{i + 1}</span>
        <Input value={s.stageName} onChange={(e) => update({ stageName: e.target.value })} placeholder="Stage name" className="h-9 flex-1 min-w-[160px]" data-testid={`stage-name-input-${i}`} />
        <label className="flex items-center gap-2 text-xs whitespace-nowrap"><Camera className="h-3.5 w-3.5" />Photos required
          <Switch checked={s.requiresPhotos} onCheckedChange={(v) => update({ requiresPhotos: v })} data-testid={`stage-photos-toggle-${i}`} /></label>
        <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">Timeline (h)
          <Input type="number" min={0} value={s.durationHours ?? 0} onChange={(e) => update({ durationHours: +e.target.value || 0 })} className="h-8 w-20" data-testid={`stage-duration-input-${i}`} /></label>
        <label className="flex items-center gap-2 text-xs whitespace-nowrap">Procurement list
          <Switch checked={!!s.showProcurementChecklist} onCheckedChange={(v) => update({ showProcurementChecklist: v })} data-testid={`stage-procure-list-toggle-${i}`} /></label>
        <label className="flex items-center gap-2 text-xs whitespace-nowrap">Assembly list
          <Switch checked={!!s.showAssemblyChecklist} onCheckedChange={(v) => update({ showAssemblyChecklist: v })} data-testid={`stage-assembly-list-toggle-${i}`} /></label>
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={i === 0} onClick={() => move(-1)} data-testid={`stage-move-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={i === total - 1} onClick={() => move(1)} data-testid={`stage-move-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={remove} data-testid={`stage-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="pl-8 space-y-1.5">
        {s.checklists.map((c, j) => (
          <div key={j} className="flex gap-2">
            <Input value={c} onChange={(e) => update({ checklists: s.checklists.map((x, k) => (k === j ? e.target.value : x)) })} placeholder="Checklist item" className="h-8 text-sm" data-testid={`stage-${i}-check-${j}`} />
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => update({ checklists: s.checklists.filter((_, k) => k !== j) })}><X className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => update({ checklists: [...s.checklists, ""] })} data-testid={`stage-${i}-add-check`}><Plus className="h-3 w-3 mr-1" />Add checklist item</Button>
      </div>
    </div>
  );
}

export default function Workflows() {
  const { user } = useAuth();
  const { data, reload } = useApi("/workflows");
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (!data) return;
    const w = data.find((x) => x.id === selId) || (selId === "new" ? null : data[0]);
    if (selId !== "new") setDraft(w ? JSON.parse(JSON.stringify(w)) : null);
  }, [data, selId]);
  if (user?.role !== "SUPER_ADMIN") return <Empty text="Only Super Admins can manage workflows." />;
  const setStage = (i, patch) => setDraft({ ...draft, stages: draft.stages.map((s, k) => (k === i ? { ...s, ...patch } : s)) });
  const move = (i, d) => {
    const st = [...draft.stages];
    [st[i], st[i + d]] = [st[i + d], st[i]];
    setDraft({ ...draft, stages: st });
  };
  const save = async () => {
    const body = { name: draft.name, isDefault: draft.isDefault, stages: draft.stages.map((s, i) => ({ ...s, sequenceOrder: i + 1, checklists: s.checklists.filter((c) => c.trim()) })) };
    if (!body.name.trim() || body.stages.some((s) => !s.stageName.trim())) return toast.error("Workflow and stage names are required");
    try {
      const r = draft.id ? await api.put(`/workflows/${draft.id}`, body) : await api.post("/workflows", body);
      toast.success("Workflow saved");
      setSelId(r.data.id);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    try { await api.delete(`/workflows/${draft.id}`); toast.success("Workflow deleted"); setSelId(null); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Workflow Templates" subtitle="Define build stages, checklist gates and photo-proof rules.">
        <Button variant="outline" onClick={() => { setSelId("new"); setDraft({ id: null, name: "", isDefault: false, stages: [newStage(1)] }); }} data-testid="new-workflow-button"><Plus className="h-4 w-4 mr-2" />New workflow</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-4 gap-6">
        <div className="space-y-1.5" data-testid="workflow-list">
          {data?.map((w) => (
            <button key={w.id} onClick={() => setSelId(w.id)} data-testid={`workflow-item-${w.name.replace(/\W+/g, "-").toLowerCase()}`}
              className={`w-full text-left rounded-md border px-3 py-2.5 text-sm transition-colors ${draft?.id === w.id ? "bg-secondary/60 border-foreground/25" : "hover:bg-secondary/30"}`}>
              <div className="flex items-center gap-2 font-medium">{w.name}{w.isDefault && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}</div>
              <div className="text-xs text-muted-foreground">{w.stages.length} stages</div>
            </button>
          ))}
        </div>
        {draft && (
          <Panel className="lg:col-span-3" title={draft.id ? "Edit workflow" : "New workflow"}
            action={<div className="flex gap-2">{draft.id && !draft.isDefault && <Button size="sm" variant="ghost" onClick={del} data-testid="delete-workflow-button"><Trash2 className="h-4 w-4" /></Button>}
              <Button size="sm" onClick={save} data-testid="save-workflow-button"><Save className="h-4 w-4 mr-2" />Save</Button></div>}>
            <div className="p-5 space-y-5">
              <div className="flex items-end gap-4">
                <div className="flex-1 space-y-1.5"><Label>Workflow name</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="workflow-name-input" /></div>
                <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={draft.isDefault} onCheckedChange={(v) => setDraft({ ...draft, isDefault: v })} data-testid="workflow-default-toggle" />Default {draft.isDefault && <Tone tone="amber">Default</Tone>}</label>
              </div>
              <div className="space-y-3">
                {draft.stages.map((s, i) => <StageEditor key={i} s={s} i={i} total={draft.stages.length} update={(p) => setStage(i, p)} move={(d) => move(i, d)}
                  remove={() => setDraft({ ...draft, stages: draft.stages.filter((_, k) => k !== i) })} />)}
              </div>
              <Button variant="outline" size="sm" onClick={() => setDraft({ ...draft, stages: [...draft.stages, newStage(draft.stages.length + 1)] })} data-testid="add-stage-button"><Plus className="h-4 w-4 mr-1" />Add stage</Button>
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}
